import React from 'react';

import { Button } from '@components/Button';
import { ManagementFormContainer } from '@components/NextForms/ManagementForm';
import { ContactInput } from '@components/utilities/Contact/ContactInput';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import Checkbox from '@material-ui/core/Checkbox';
import { ContactFormItem, ContactFormItemAction } from '@models/next/Contact';
import { BaseContactForm, ContactType } from '@models/next/Contact';
import { MobxZodObjectField } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    sameAsOwner: (c: ContactType) => `${bookingInquiryStrings[c]}情報と同じ`,
    sameAsCompany: '会社情報と同じ',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    padding: '11px 3rem',
    borderRadius: 10,
    border: '1px solid #F2F2F2',
    boxShadow: '0px 4px 45px -9px rgba(238, 238, 238, 0.5)',
    columnGap: '1rem',
    '&$editing': {
      borderColor: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '11px 1rem',
    },
  },
  buttonsContainer: {
    ...common.flexInline,
    alignItems: 'flex-start',
    columnGap: 11,
    padding: '12px 0',
  },
  contactHeader: {
    ...common.middleText,
    ...common.textPrimary,
  },
  contactSameAsOwnerCheckbox: {
    ...common.middleText,
    ...common.textGray700,
    ...common.flexInline,
  },
  editContainer: {
    ...common.flexColumn,
    flex: 1,
    padding: '12px 0',
  },
  editing: {},
}));

export interface ContactDisplayEditCardProps {
  /**
   * Are we creating a new contact with owned contacts, or modifying a current contact?
   */
  ownerAction: ContactFormItemAction;
  item: ContactFormItem;
  type: ContactType;
  field: MobxZodObjectField<typeof BaseContactForm>;
  ownedContactIndex?: number;
  sameAsOwnerDisabled?: boolean;
  onChangeSameAsOwner?: (value: boolean) => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
}

export const ContactDisplayEditCard: React.FC<ContactDisplayEditCardProps> = ({
  ownerAction,
  item,
  type,
  field,
  ownedContactIndex,
  sameAsOwnerDisabled,
  onChangeSameAsOwner,
  onEdit,
  onCancel,
  onDelete,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isOwner = type === 'consignee' || type === 'shipper';

  const ownerContactType: ContactType = type === 'pickup_address' ? 'shipper' : 'consignee';

  const { mode } = item;

  const renderContactHeader = () => {
    return (
      <>
        <div className={classes.contactHeader}>
          {bookingInquiryStrings[type]} {(ownedContactIndex ?? 0) + 1}
        </div>
        <div className={classes.contactSameAsOwnerCheckbox}>
          {mode !== 'display' && (
            <span className={classes.flexInline}>
              <Checkbox
                checked={item.mode === 'sameAsOwner'}
                onChange={(e) => {
                  onChangeSameAsOwner!(e.target.checked);
                }}
                color="primary"
                style={{
                  padding: 0,
                  paddingRight: 6,
                  position: 'relative',
                  top: -1,
                }}
                disabled={(item.action === 'edit' && item.displayOnly) || sameAsOwnerDisabled}
              />
              {strings.sameAsOwner(ownerContactType)}
            </span>
          )}

          <div style={{ flex: 1 }} />

          {ownerAction === 'create' && (
            <Button
              text={formStrings.delete}
              type="button"
              color="grey"
              size="small"
              shape="round"
              onClick={() => onDelete!()}
              style={{ marginTop: -5, marginBottom: -5 }}
            />
          )}
        </div>
      </>
    );
  };

  const renderEditButtons = () => {
    if (item.action === 'edit' && item.displayOnly) {
      return null;
    }
    return (
      <div className={classes.buttonsContainer}>
        {(item.action === 'edit' || item.action === 'create') && item.mode === 'edit' && (
          <>
            <Button
              text={formStrings.save}
              type="submit"
              color="primary"
              size="small"
              shape="round"
            />
            <Button
              text={formStrings.cancel}
              type="button"
              color="grey"
              size="small"
              shape="round"
              onClick={onCancel}
            />
          </>
        )}

        {item.action === 'edit' && item.mode === 'display' && (
          <>
            <Button
              text={formStrings.edit}
              type="button"
              color="primary"
              size="small"
              shape="round"
              onClick={onEdit}
            />
            <Button
              text={formStrings.delete}
              type="button"
              color="grey"
              size="small"
              shape="round"
              onClick={onDelete}
            />
          </>
        )}

        {item.action === 'edit' && item.mode === 'sameAsOwner' && (
          <>
            <Button
              text={formStrings.delete}
              type="button"
              color="grey"
              size="small"
              shape="round"
              onClick={onDelete}
              style={{ marginTop: -5, marginBottom: -5 }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div
      role="ContactDisplayEditCard"
      data-category={type}
      className={clsx(classes.container, mode === 'edit' && classes.editing)}
    >
      <div className={classes.editContainer}>
        {(mode === 'display' || mode === 'edit') && (
          <ContactInput
            field={field}
            mode={mode}
            type={type}
            firstChild={!isOwner && renderContactHeader()}
            item={item}
          />
        )}

        {mode === 'sameAsOwner' && (
          <ManagementFormContainer mode="display">{renderContactHeader()}</ManagementFormContainer>
        )}
      </div>
      {ownerAction === 'edit' && renderEditButtons()}
    </div>
  );
};
